import React, { useContext, useMemo } from "react";
import { Trash2 } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";
import { LanguageContext } from "../../constant/LanguageContext";

// Truncate name to first two parts for cleaner display
const truncateName = (name) => {
  if (!name) return name;
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).join(' ');
};

const NotificationItem = ({ notification, onClick }) => {
  const { markAsRead, deleteNotification } = useNotifications();
  const { language } = useContext(LanguageContext);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!notification.read) {
      markAsRead(notification.id);
    }
    onClick();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    deleteNotification(notification.id);
  };

  const formatTimeAgo = useMemo(() => {
    const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
    const date = new Date(notification.createdAt);

    return () => {
      const daysDifference = Math.round((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      if (Math.abs(daysDifference) > 7) {
        return new Intl.DateTimeFormat(language, {
          month: "short",
          day: "numeric",
          year: "numeric"
        }).format(date);
      }
      if (daysDifference === 0) {
        const hoursDifference = Math.round((date.getTime() - new Date().getTime()) / (1000 * 60 * 60));
        if (hoursDifference === 0) {
          const minutesDifference = Math.round((date.getTime() - new Date().getTime()) / (1000 * 60));
          return rtf.format(minutesDifference, "minute");
        }
        return rtf.format(hoursDifference, "hour");
      }
      return rtf.format(daysDifference, "day");
    };
  }, [notification.createdAt, language]);

  // Exact date + time of the notification, always visible next to the
  // relative time — every notification must show WHEN it happened.
  const exactDateTime = useMemo(() => {
    const date = new Date(notification.createdAt);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }, [notification.createdAt, language]);

  const getAccentColor = () => {
    const type = notification.type || "";
    if (type.includes("LEAVE")) return { color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.1)" };
    if (type.includes("DOCUMENT")) return { color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" };
    if (type.includes("CALENDAR") || notification.eventId) return { color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" };
    if (type.includes("MESSAGE")) return { color: "#06b6d4", bg: "rgba(6, 182, 212, 0.1)" };
    if (type.includes("ADVANCEMENT")) return { color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
    if (type.includes("PROFILE")) return { color: "#ec4899", bg: "rgba(236, 72, 153, 0.1)" };
    if (type.includes("EMPLOYEE")) return { color: "#ec4899", bg: "rgba(236, 72, 153, 0.1)" };
    if (type.includes("REPORT")) return { color: "#0891b2", bg: "rgba(8, 145, 178, 0.1)" };
    if (type.includes("ESCALATION")) return { color: "#f43f5e", bg: "rgba(244, 63, 94, 0.1)" };
    if (type.includes("WORKFLOW")) return { color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" };
    if (type.includes("PAYROLL")) return { color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
    if (type.includes("ATTENDANCE")) return { color: "#f97316", bg: "rgba(249, 115, 22, 0.1)" };
    if (type.includes("PASSWORD") || type.includes("PERMISSION") || type.includes("ADMIN_LEVEL")) return { color: "#64748b", bg: "rgba(100, 116, 139, 0.1)" };
    if (type.includes("CONFIG")) return { color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" };
    if (type.includes("WELCOME")) return { color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
    if (type.includes("APPROVED") || type.includes("SUCCESS")) return { color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" };
    if (type.includes("REJECTED") || type.includes("ERROR")) return { color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" };
    if (type.includes("ACTION")) return { color: "#f97316", bg: "rgba(249, 115, 22, 0.1)" };
    return { color: "#6366f1", bg: "rgba(99, 102, 241, 0.1)" };
  };

  const { color, bg } = getAccentColor();

  // Build a type label badge
  const getTypeBadge = () => {
    const type = notification.type || "";
    const isAr = language === "ar";
    if (type.includes("LEAVE")) return isAr ? "إجازة" : "Leave";
    if (type.includes("DOCUMENT")) return isAr ? "مستند" : "Document";
    if (type.includes("CALENDAR")) return isAr ? "تقويم" : "Calendar";
    if (type.includes("ADVANCEMENT")) return isAr ? "سلفة" : "Advancement";
    if (type.includes("PROFILE")) return isAr ? "الملف الشخصي" : "Profile";
    if (type.includes("EMPLOYEE")) return isAr ? "موظف" : "Employee";
    if (type.includes("MESSAGE")) return isAr ? "رسالة" : "Message";
    if (type.includes("REPORT")) return isAr ? "تقرير" : "Report";
    if (type.includes("ESCALATION")) return isAr ? "شكوى" : "Complaint";
    if (type.includes("WORKFLOW")) return isAr ? "موافقة" : "Approval";
    if (type.includes("PAYROLL")) return isAr ? "الرواتب" : "Payroll";
    if (type.includes("ATTENDANCE")) return isAr ? "الحضور" : "Attendance";
    if (type.includes("PASSWORD") || type.includes("PERMISSION") || type.includes("ADMIN_LEVEL")) return isAr ? "الحساب" : "Account";
    if (type.includes("CONFIG")) return isAr ? "الشركة" : "Company";
    if (type.includes("WELCOME")) return isAr ? "مرحبًا" : "Welcome";
    if (type.includes("GROUP")) return isAr ? "مجموعة" : "Group";
    return null;
  };

  const typeBadge = getTypeBadge();

  return (
    <div 
      className={`notification-item ${!notification.read ? "unread" : ""}`}
      onClick={handleClick}
    >
      <div className="notification-content">
        <div className="notification-item-header">
          {typeBadge && (
            <span className="notification-type-badge" style={{ background: bg, color }}>
              {typeBadge}
            </span>
          )}
          {notification.fromName && (
            <span className="notification-sender">{truncateName(notification.fromName)}</span>
          )}
        </div>
        <h4 className="notification-item-title">{notification.title}</h4>
        {notification.message && notification.message !== notification.title && (
          <p className="notification-item-desc">{notification.message}</p>
        )}
        <div className="notification-footer">
          <span className="notification-time">{formatTimeAgo()}</span>
          {exactDateTime && (
            <span className="notification-datetime" title={exactDateTime}>
              {exactDateTime}
            </span>
          )}
        </div>
      </div>
      
      <div className="notification-actions">
        {!notification.read && <div className="notification-dot" />}
        <button 
          className="notification-delete-btn" 
          onClick={handleDelete}
          title={language === "ar" ? "حذف الإشعار" : "Delete Notification"}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;
